import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { FilterOption } from '@/components/UI/Table/TableComponent.model';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { Study } from '@/model/Study.model';
import { ResponseType } from '@/support/ApiService';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import Http from '@/support/http';
import { api as apiRoutes } from '@/support/route';
import { modalAction } from '@/utils/modal';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { columns, formatFilterOptions, ObservationWithRelations } from './Observations.utils';
import * as Auth from '@/support/auth.ts';
import { ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList.tsx';

interface FilterInput {
  value: string;
  options?: Array<{ id: ID; name: string }>;
}

const Show: FC = () => {
  const { id: studyId } = useParams<{ id: string }>();
  const { entity: study, entityLoading: studyLoading } = useFetchEntity<Study>({
    entityType: 'study',
    params: { id: studyId },
  });

  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [errors, setErrors] = useState<Error | null>(null);
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);
  const isWriteUser = _notNil(study) ? Auth.isWriteUserForStudy(study) : false;

  const { invoke: deleteObservations } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/observations/remove',
    invokeOnInit: false,
  });

  const getFilterOptions = () => {
    if (_notNil(study)) {
      Http.get<Array<FilterInput>>(
        apiRoutes('studies.observations.filter.get', {
          id: study.id,
        })
      )
        .then(({ data }) => setFilterOptions(formatFilterOptions(data)))
        .catch((error: Error) => {
          setErrors(error);
        });
    }
  };

  useEffect(() => {
    if (_notNil(study)) {
      getFilterOptions();
    }
  }, [study]);

  const memoizedColumns = useMemo(() => (_notNil(study) ? columns(study.id) : []), [study?.id]);

  const exportRoutes = useMemo(
    () => [
      {
        label: 'All observations',
        url: 'studies.observations.export',
        params: { id: study?.id },
        noFilters: true,
      },
      {
        label: 'Current filter selection',
        url: 'studies.observations.export',
        params: { id: study?.id },
      },
    ],
    [study?.id]
  );

  const handleBulkDelete = async (
    observationApiIds: Array<string>,
    reasonForChange: string,
    refetchTableData: () => Promise<void>
  ) => {
    if (_notNil(study)) {
      const response = await deleteObservations({
        path: { studyApiId: study.api_id },
        body: {
          observation_api_ids: observationApiIds,
          reason_for_change: reasonForChange,
        },
      });

      if (response.type === ResponseType.Success) {
        successToast('Successfully deleted observations');
        closeModal();
        await refetchTableData();
      } else {
        errorToast('Failed to delete observations');
      }
    }
  };

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: UseTableProps<ObservationWithRelations>): Array<{ name: string; action: () => void; className?: string }> => {
    const observations = selectedFlatRows.map((row) => row.original);
    const observationApiIds = observations.map((obs) => obs.api_id);

    if (!isWriteUser) {
      return [];
    }

    const actions: Array<ActionProps> = [
      {
        name: 'Delete',
        className: 'red',
        action: () =>
          openModal('CONFIRM_DELETE_OBSERVATION', {
            onClick: (reasonForChange: string) =>
              handleBulkDelete(observationApiIds, reasonForChange, refetchTableData),
            closeModal,
          }),
      },
    ];
    return actions;
  };

  if (studyLoading) {
    return null;
  }

  return (
    <>
      {_notNil(errors) ? (
        <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={errors} />
      ) : (
        <div className="ph4 pt3">
          {_notNil(study) && (
            <APITable
              columns={memoizedColumns}
              apiInfo={{
                type: 'legacyInternalApi',
                route: apiRoutes('studies.observations.show', {
                  id: study.id,
                }),
              }}
              reduxTableName="observations"
              includeParam="study_group"
              pageSizes={[50, 100, 200, 500]}
              showViewSelector
              defaultPageSize={50}
              filterOptions={filterOptions}
              exportRoutes={exportRoutes}
              searchPlaceholderText="Search by animal name or observation"
              bulkActions={bulkActions}
              testIdPrefix="observations"
            />
          )}
        </div>
      )}
    </>
  );
};

export default Show;
